// simulator/api/agendaLlenaApi.js
// Espejo EN MEMORIA de apps/appointments/src/api/agendaLlenaApi.js (c_clientInsightsApi) para el simulador de
// ventas: mismas funciones y misma forma de respuesta, pero NINGUNA llamada de red (no importa axiosClient).
// Los componentes copiados de «Tu agenda llena» (ListaClientesModal, EnviarModal, AutomaticoSwitch) importan
// "../../api/agendaLlenaApi" y aquí lo encuentran.
//
// Reglas que se imitan del backend:
//   - Un cliente avisado hace menos de 30 días no se vuelve a avisar (saltado `avisado_30d`).
//   - Como mucho MAX_DIA (100) envíos por empresa y día: el resto queda para los días siguientes (`tope_diario`).
//   - 0,06 € por aviso (marketing, aparte de los 1.000 incluidos).
//   - «perdido» avisa a los dos grupos: los que se retrasan y los perdidos.
//   - «No volver a avisar» (setNoAvisar) marca al cliente y el envío lo salta (`no_avisar`), también en dry_run.
import { buildAgendaLlenaDemo, explicaAgenda } from "../utils/agendaLlenaDemo";
import { MAX_DIA, TOPE_DEFECTO_EUR, hoyMadrid } from "../components/agendaLlena/agendaTextos";

export const PRECIO_AVISO_EUR = 0.06;
const DIA_MS = 24 * 3600 * 1000;
const TARJETAS_AUTO = ["volver", "nuevo", "perdido"];

// El envío real tarda un poco para que se vea «Enviando...»; los tests lo ponen a 0.
let delayEnvioMs = 600;
export const setDelayEnvio = (ms) => { delayEnvioMs = ms; };

let estado = null;
let ultimaConfig = { workers: [], services: [] };

const mesActual = () => new Date().toISOString().slice(0, 7);
const firma = (cfg, hoy) => `${hoy}|${(cfg.workers || []).map((w) => `${w.id || w.worker_id || w.name}`).join(",")}`;

function automaticoInicial() {
  const out = {};
  for (const t of TARJETAS_AUTO) out[t] = { enabled: false, tope_eur: TOPE_DEFECTO_EUR, oferta: "", gastado_mes: { mes: mesActual(), eur: 0 } };
  return out;
}

function asegurar() {
  const hoy = hoyMadrid();
  const key = firma(ultimaConfig, hoy);
  if (!estado || estado.key !== key) {
    estado = {
      key,
      demo: buildAgendaLlenaDemo({ ...ultimaConfig, hoy }),
      avisos: {}, // phone -> ISO del último aviso
      noAvisar: {}, // phone -> true si el dueño pidió no volver a avisarle
      enviadosHoy: 0,
      vueltas: null,
      automatico: automaticoInicial(),
    };
    estado.vueltas = { ...estado.demo.vueltas_mes };
  }
  return estado;
}

/** La pantalla pasa aquí los trabajadores y servicios del simulador antes de pedir datos. */
export function configurarAgendaLlena(cfg) {
  ultimaConfig = { workers: (cfg && cfg.workers) || [], services: (cfg && cfg.services) || [] };
  asegurar();
}

/** Vuelve a lo de fábrica (tests y «Salir del simulador»). */
export function resetAgendaLlena() {
  estado = null;
  ultimaConfig = { workers: [], services: [] };
}

const conAviso = (c, e) => ({ ...c, ...(e.avisos[c.phone] ? { aviso_at: e.avisos[c.phone] } : {}), no_avisar: e.noAvisar[c.phone] === true });
const avisadoReciente = (iso) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) && Date.now() - t < 30 * DIA_MS;
};

function clientesDe(e, tarjeta, trabajador) {
  const l = e.demo.listas;
  const base = tarjeta === "perdido" ? [...l.retrasado, ...l.perdido] : (l[tarjeta] || []);
  return base.filter((c) => !trabajador || c.trabajador === trabajador).map((c) => conAviso(c, e));
}

function destinatarios(e, { tarjeta, trabajador, hueco }) {
  if (tarjeta === "hueco") {
    const h = (e.demo.resumen.huecos || []).find((x) => hueco && x.day === hueco.day && x.franja === hueco.franja && x.worker === hueco.worker);
    if (!h) { const err = new Error("hueco"); err.response = { status: 400, data: { error: "hueco" } }; throw err; }
    const por = new Map(Object.values(e.demo.listas).flat().map((c) => [c.phone, c]));
    return (h.candidatos || []).map((p) => por.get(p)).filter(Boolean).map((c) => conAviso(c, e));
  }
  return clientesDe(e, tarjeta, trabajador);
}

// Lo enviado desde la demo suma también al embudo del mes actual (la cabecera y «Resultados de tus avisos» no se
// separan): en la demo todo lo enviado se entrega; lo leído y lo reservado no llega hasta más tarde.
function sumarAlEmbudo(e, tarjeta, n) {
  if (!n) return;
  const mes = e.demo.embudo[hoyMadrid().slice(0, 7)];
  if (!mes) return;
  const fila = mes[tarjeta] || (mes[tarjeta] = { enviados: 0, entregados: 0, leidos: 0, fallidos: 0, abrieron: 0, reservaron: 0, ingresos_eur: 0, importes_estimados: 0, coste_eur: 0 });
  for (const f of [fila, mes.total]) {
    f.enviados += n; f.entregados += n;
    f.coste_eur = Math.round(f.enviados * PRECIO_AVISO_EUR * 100) / 100;
  }
}

const copia = (o) => JSON.parse(JSON.stringify(o));

/** Resumen del cálculo + ajustes del automático. Nunca bloqueado: la demo enseña la pantalla de un cliente de pago. */
export async function getResumen() {
  const e = asegurar();
  return copia({
    resumen: e.demo.resumen,
    automatico: e.automatico,
    vueltas_mes: e.vueltas,
    embudo: e.demo.embudo,
    bloqueado: false,
  });
}

/**
 * Ejemplos de la cabecera «Qué detecta la IA» (solo simulador), de los mismos datos que las tarjetas y con el mismo
 * filtro de trabajador. Síncrono: todo está en memoria.
 */
export function getExplicacion(trabajador) {
  const e = asegurar();
  return explicaAgenda({ listas: e.demo.listas, resumen: e.demo.resumen, hoy: hoyMadrid(), trabajador: trabajador || "", nWorkers: e.demo.workers.length });
}

/** Clientes de una tarjeta (volver, nuevo, perdido, retrasado), opcionalmente de un solo trabajador. */
export async function getLista(tarjeta, trabajador) {
  const e = asegurar();
  return { clientes: clientesDe(e, tarjeta, trabajador || "") };
}

/** Avisa a una tarjeta. `dry_run: true` solo calcula el coste y no toca nada. */
export async function enviar(p) {
  const e = asegurar();
  const fuera = new Set((p && p.excluir) || []);
  const todos = destinatarios(e, p).filter((c) => !fuera.has(c.phone));
  const bajas = todos.filter((c) => c.no_avisar === true);
  const avisados = todos.filter((c) => c.no_avisar !== true && avisadoReciente(c.aviso_at));
  const aEnviar = todos.filter((c) => c.no_avisar !== true && !avisadoReciente(c.aviso_at));
  const hoy = Math.max(0, Math.min(aEnviar.length, MAX_DIA - e.enviadosHoy));
  const saltados = {};
  if (bajas.length) saltados.no_avisar = bajas.length;
  if (avisados.length) saltados.avisado_30d = avisados.length;

  if (p && p.dry_run === true) {
    return {
      dry_run: true,
      enviables: aEnviar.length,
      enviables_hoy: hoy,
      coste_eur: Math.round(aEnviar.length * PRECIO_AVISO_EUR * 100) / 100,
      coste_hoy_eur: Math.round(hoy * PRECIO_AVISO_EUR * 100) / 100,
      saltados,
    };
  }

  if (delayEnvioMs > 0) await new Promise((r) => setTimeout(r, delayEnvioMs));
  const ahora = new Date().toISOString();
  for (const c of aEnviar.slice(0, hoy)) e.avisos[c.phone] = ahora;
  e.enviadosHoy += hoy;
  e.vueltas = { ...e.vueltas, sent: e.vueltas.sent + hoy };
  sumarAlEmbudo(e, p && p.tarjeta, hoy);
  if (aEnviar.length > hoy) saltados.tope_diario = aEnviar.length - hoy;
  return { enviados: hoy, saltados };
}

/** Enciende/apaga el envío diario de una tarjeta, con su tope de gasto mensual y su oferta. */
export async function setAutomatico(p) {
  const e = asegurar();
  const tarjeta = p && p.tarjeta;
  if (!TARJETAS_AUTO.includes(tarjeta)) { const err = new Error("tarjeta"); err.response = { status: 400, data: { error: "tarjeta" } }; throw err; }
  const topeNum = Number(p.tope_eur);
  const tope = Number.isFinite(topeNum) ? Math.min(1000, Math.max(0, topeNum)) : TOPE_DEFECTO_EUR;
  const oferta = String(p.oferta || "").replace(/\s+/g, " ").trim().slice(0, 60);
  e.automatico[tarjeta] = { ...e.automatico[tarjeta], enabled: p.enabled === true, tope_eur: tope, oferta };
  return { ok: true, tarjeta, enabled: p.enabled === true, tope_eur: tope, oferta };
}

/** «No volver a avisar» (true) / «Volver a avisar» (false) a un cliente de la demo. Solo en memoria. */
export async function setNoAvisar(phone, valor) {
  const e = asegurar();
  const existe = Object.values(e.demo.listas).flat().some((c) => c.phone === phone);
  if (!existe) { const err = new Error("cliente"); err.response = { status: 404, data: { error: "cliente" } }; throw err; }
  if (valor === true) e.noAvisar[phone] = true; else delete e.noAvisar[phone];
  return { ok: true, phone, no_avisar: valor === true };
}
